import catalog from './propertyTypeCatalog.json';

export { catalog as PROPERTY_TYPE_CATALOG };

export function parseTypeDetails(raw) {
  if (raw == null || raw === '') return {};
  if (typeof raw === 'object' && !Array.isArray(raw)) return { ...raw };
  if (typeof raw !== 'string') return {};
  try {
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
  } catch {
    return {};
  }
}

export function findCategory(catalogData, categoryId) {
  const cats = catalogData?.categories || catalog.categories;
  return cats.find((c) => c.id === categoryId) || null;
}

export function findType(catalogData, typeId) {
  const types = catalogData?.types || catalog.types;
  return types.find((t) => t.id === typeId) || null;
}

export function resolvePropertyType(catalogData, building) {
  const data = catalogData || catalog;
  const types = data.types || [];
  const id = building?.property_type_id;
  if (id) {
    const byId = types.find((t) => t.id === id);
    if (byId) return byId;
  }
  const label = String(building?.property_type || '').trim();
  if (!label) return null;
  const lower = label.toLowerCase();
  return (
    types.find((t) => t.id === label || t.label.toLowerCase() === lower) ||
    types.find((t) => (t.aliases || []).some((a) => String(a).toLowerCase() === lower)) ||
    types.find((t) => lower.includes(t.label.toLowerCase()) || t.label.toLowerCase().includes(lower)) ||
    null
  );
}

export function hydrateType(catalogData, type) {
  if (!type) return null;
  const data = catalogData || catalog;
  const category = findCategory(data, type.category) || findCategory(data, 'other');
  return {
    ...category,
    ...type,
    occupancyNoun: type.occupancyNoun || category?.occupancyNoun || 'unit',
    occupancyNounPlural: type.occupancyNounPlural || category?.occupancyNounPlural || 'units',
    sections: type.sections || category?.sections || [],
    amenities: type.amenities || category?.amenities || [],
    extraFields: type.extraFields || category?.extraFields || [],
    categoryLabel: category?.label || type.category,
    categoryIcon: category?.icon || 'fa-building',
  };
}

export function typesByCategory(catalogData) {
  const data = catalogData || catalog;
  const map = {};
  for (const cat of data.categories || []) {
    map[cat.id] = (data.types || []).filter((t) => t.category === cat.id);
  }
  return map;
}

export function emptyBuildingForm(companyId = '') {
  return {
    name: '',
    building_name: '',
    property_type: '',
    property_type_id: '',
    property_category: '',
    year_built: '',
    total_floor_area: '',
    total_units: '',
    number_of_floors: '',
    occupied_units: '',
    vacant_units: '',
    total_parking: '',
    visitor_parking: '',
    covered_parking: '',
    parking_type: '',
    ev_charging: '',
    heating_system: '',
    cooling_system: '',
    elevators: '',
    water_heater: '',
    laundry_facilities: '',
    fitness_center: '',
    swimming_pool: '',
    hot_tub: '',
    sauna: '',
    community_room: '',
    business_center: '',
    rooftop_terrace: '',
    playground: '',
    pet_facilities: '',
    security_system: '',
    access_control: '',
    concierge: '',
    street_number: '',
    street_name: '',
    city: '',
    postal_code: '',
    country: 'Canada',
    country_code: '+1',
    phone_number: '',
    email: '',
    web_address: '',
    latitude: '',
    longitude: '',
    additional_notes: '',
    company_id: companyId || '',
    type_details: {},
  };
}

export function buildingToForm(building, companyId, catalogData) {
  const base = emptyBuildingForm(companyId);
  if (!building) return base;
  const resolved = hydrateType(catalogData, resolvePropertyType(catalogData, building));
  const details = parseTypeDetails(building.type_details);
  return {
    ...base,
    ...Object.fromEntries(
      Object.keys(base).map((key) => [key, building[key] != null ? building[key] : base[key]])
    ),
    company_id: building.company_id || companyId || '',
    property_type_id: resolved?.id || building.property_type_id || '',
    property_category: resolved?.category || building.property_category || '',
    property_type: resolved?.label || building.property_type || '',
    type_details: details,
  };
}

export function applyTypeDefaults(form, hydratedType) {
  if (!hydratedType) return form;
  const next = {
    ...form,
    property_type_id: hydratedType.id,
    property_category: hydratedType.category,
    property_type: hydratedType.label,
  };
  const defaults = hydratedType.defaults || {};
  for (const [key, value] of Object.entries(defaults)) {
    if (next[key] === '' || next[key] == null) next[key] = value;
  }
  return next;
}

export function formatOccupancyCount(building, catalogData) {
  const type = hydrateType(catalogData, resolvePropertyType(catalogData, building));
  const n = building?.total_units;
  if (n == null || n === '') return '';
  const noun = Number(n) === 1
    ? (type?.occupancyNoun || 'unit')
    : (type?.occupancyNounPlural || 'units');
  return `${n} ${noun}`;
}
